import { FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type AccessMode = 'login' | 'register';
type RegisterField = 'name' | 'email' | 'contactPhone' | 'password';

const NAME_PATTERN = /^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s'.-]*$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGIT_PATTERN = /\D/g;

const formatContactPhone = (value: string) => {
  const digits = value.replace(PHONE_DIGIT_PATTERN, '').slice(0, 13);
  const hasCountryCode = digits.startsWith('55') && digits.length > 11;
  const prefix = hasCountryCode ? '+55 ' : '';
  const localDigits = hasCountryCode ? digits.slice(2) : digits;
  const areaCode = localDigits.slice(0, 2);
  const number = localDigits.slice(2);

  if (localDigits.length <= 2) return `${prefix}${areaCode}`;
  if (number.length <= 4) return `${prefix}(${areaCode}) ${number}`;

  const firstPartLength = number.length > 8 ? 5 : 4;
  const firstPart = number.slice(0, firstPartLength);
  const secondPart = number.slice(firstPartLength, firstPartLength + 4);
  return `${prefix}(${areaCode}) ${firstPart}${secondPart ? `-${secondPart}` : ''}`;
};

const validateName = (name: string) => {
  const trimmed = name.trim();
  if (trimmed.length < 2) return 'Informe seu nome completo.';
  if (trimmed.length > 255) return 'Nome deve ter no máximo 255 caracteres.';
  if (!NAME_PATTERN.test(trimmed)) {
    return 'Use apenas letras, espaços, ponto, apóstrofo ou hífen no nome.';
  }

  return '';
};

const validateContactPhone = (phone: string) => {
  const digits = phone.replace(PHONE_DIGIT_PATTERN, '');
  if (digits.length < 10 || digits.length > 13) return 'Informe um telefone válido com DDD.';
  return '';
};

const validateEmail = (email: string) => {
  const trimmed = email.trim();
  if (!EMAIL_PATTERN.test(trimmed)) return 'Informe um e-mail válido.';
  if (trimmed.length > 320) return 'E-mail deve ter no máximo 320 caracteres.';
  return '';
};

const validatePassword = (password: string, name: string, email: string) => {
  if (password.trim() !== password) return 'A senha não pode começar ou terminar com espaços.';
  if (password.length < 8) return 'A senha deve ter pelo menos 8 caracteres.';
  if (password.length > 128) return 'A senha deve ter no máximo 128 caracteres.';
  if (/\s/.test(password)) return 'A senha não pode conter espaços.';
  if (!/[A-Za-zÀ-ÿ]/.test(password) || !/\d/.test(password)) {
    return 'A senha deve combinar letras e números.';
  }

  const lowered = password.toLowerCase();
  const emailPart = email.trim().toLowerCase().split('@')[0];
  if (emailPart.length >= 4 && lowered.includes(emailPart)) {
    return 'A senha não pode conter parte do e-mail.';
  }
  const nameParts = name.trim().toLowerCase().split(/\s+/).filter((part) => part.length >= 4);
  if (nameParts.some((part) => lowered.includes(part))) {
    return 'A senha não pode conter parte do nome.';
  }

  return '';
};

const validateRegisterFields = (form: {
  name: string;
  email: string;
  contactPhone: string;
  password: string;
}) => {
  const errors: Partial<Record<RegisterField, string>> = {};
  const nameError = validateName(form.name);
  const emailError = validateEmail(form.email);
  const phoneError = validateContactPhone(form.contactPhone);
  const passwordError = validatePassword(form.password, form.name, form.email);

  if (nameError) errors.name = nameError;
  if (emailError) errors.email = emailError;
  if (phoneError) errors.contactPhone = phoneError;
  if (passwordError) errors.password = passwordError;

  return errors;
};

const Access = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<AccessMode>('login');
  const [form, setForm] = useState({ name: '', email: '', contactPhone: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<RegisterField, string>>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

  const finishAccess = (role: 'criador' | 'admin') => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
      return;
    }

    navigate(role === 'admin' ? '/admin' : '/portal', { replace: true });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setFieldErrors({});

    try {
      if (mode === 'register') {
        const registerErrors = validateRegisterFields(form);
        if (Object.keys(registerErrors).length > 0) {
          setFieldErrors(registerErrors);
          throw new Error(Object.values(registerErrors)[0] || 'Revise os campos destacados.');
        }
      }

      const user =
        mode === 'login'
          ? await login({ email: form.email, password: form.password })
          : await register({
              name: form.name.trim(),
              email: form.email.trim().toLowerCase(),
              contactPhone: form.contactPhone,
              password: form.password,
            });

      finishAccess(user.role);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : 'Não foi possível completar o acesso.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="access-page">
      <div className="container py-5">
        <div className="row g-4 align-items-start">
          <div className="col-lg-5">
            <div className="glass-card p-4 h-100">
              <span className="badge bg-brand text-white fw-semibold px-3 py-2 rounded-pill mb-3">
                Portal do Solicitante
              </span>
              <h1 className="text-white mb-3">Acesse ou crie sua conta</h1>
              <p className="text-white-50 mb-4">
                Entre com seu e-mail e senha ou crie um cadastro rápido para enviar eventos e
                acompanhar os pedidos aprovados na agenda de inovação.
              </p>
              <ul className="text-white-50 small mb-4">
                <li>Cadastro com contato direto para retorno da curadoria.</li>
                <li>Envie novos eventos e acompanhe solicitações em um único lugar.</li>
                <li>Ambiente restrito para a equipe e parceiros de inovação.</li>
              </ul>
              <p className="text-white-50 small mb-0">
                Se o ambiente estiver vazio, crie sua conta normalmente e use o bootstrap de
                backoffice para promover o primeiro administrador.
              </p>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="card bg-onyx border-0 shadow-lg">
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h4 className="text-white mb-1">{mode === 'login' ? 'Entrar' : 'Criar conta'}</h4>
                    <p className="text-white-50 mb-0">
                      {mode === 'login'
                        ? 'Use seu e-mail e senha para acessar o portal.'
                        : 'Cadastre-se para liberar o envio e acompanhamento de eventos.'}
                    </p>
                  </div>
                  <div className="btn-group btn-group-sm toggle-group">
                    <button
                      type="button"
                      className={`btn ${mode === 'login' ? 'btn-brand' : 'btn-outline-light'}`}
                      onClick={() => {
                        setMode('login');
                        setFieldErrors({});
                        setError('');
                      }}
                    >
                      Entrar
                    </button>
                    <button
                      type="button"
                      className={`btn ${mode === 'register' ? 'btn-brand' : 'btn-outline-light'}`}
                      onClick={() => {
                        setMode('register');
                        setFieldErrors({});
                        setError('');
                      }}
                    >
                      Criar conta
                    </button>
                  </div>
                </div>

                {error && <div className="alert alert-danger py-2">{error}</div>}

                <form className="row g-3" onSubmit={handleSubmit}>
                  {mode === 'register' && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label text-white" htmlFor="access-name">
                          Nome completo
                        </label>
                        <input
                          id="access-name"
                          className={`form-control input-contrast${fieldErrors.name ? ' is-invalid' : ''}`}
                          placeholder="Nome e sobrenome"
                          autoComplete="name"
                          minLength={2}
                          maxLength={255}
                          pattern="[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s'.-]*"
                          value={form.name}
                          onChange={(event) =>
                            setForm((current) => ({ ...current, name: event.target.value }))
                          }
                          onBlur={() =>
                            setForm((current) => ({ ...current, name: current.name.trim() }))
                          }
                          required
                        />
                        {fieldErrors.name && <div className="invalid-feedback">{fieldErrors.name}</div>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label text-white" htmlFor="access-phone">
                          Telefone de contato
                        </label>
                        <input
                          id="access-phone"
                          className={`form-control input-contrast${fieldErrors.contactPhone ? ' is-invalid' : ''}`}
                          type="tel"
                          placeholder="(34) 99999-0000"
                          autoComplete="tel"
                          inputMode="tel"
                          maxLength={19}
                          pattern="(\+55\s?)?\(?\d{2}\)?\s?\d{4,5}-?\d{4}"
                          value={form.contactPhone}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              contactPhone: formatContactPhone(event.target.value),
                            }))
                          }
                          required
                        />
                        {fieldErrors.contactPhone && (
                          <div className="invalid-feedback">{fieldErrors.contactPhone}</div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="col-md-6">
                    <label className="form-label text-white" htmlFor="access-email">
                      E-mail
                    </label>
                    <input
                      id="access-email"
                      className={`form-control input-contrast${fieldErrors.email ? ' is-invalid' : ''}`}
                      type="email"
                      placeholder="seu.email@dominio.com"
                      autoComplete="email"
                      maxLength={320}
                      pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                      value={form.email}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, email: event.target.value }))
                      }
                      onBlur={() =>
                        setForm((current) => ({
                          ...current,
                          email: current.email.trim().toLowerCase(),
                        }))
                      }
                      required
                    />
                    {fieldErrors.email && <div className="invalid-feedback">{fieldErrors.email}</div>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-white" htmlFor="access-password">
                      Senha
                    </label>
                    <input
                      id="access-password"
                      className={`form-control input-contrast${fieldErrors.password ? ' is-invalid' : ''}`}
                      type="password"
                      placeholder="Crie uma senha segura"
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      maxLength={128}
                      value={form.password}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, password: event.target.value }))
                      }
                      required
                    />
                    {mode === 'register' && (
                      <div className="form-text text-white-50">
                        Mínimo 8 caracteres, com letras e números, sem espaços.
                      </div>
                    )}
                    {fieldErrors.password && (
                      <div className="invalid-feedback">{fieldErrors.password}</div>
                    )}
                  </div>

                  <div className="col-12 d-flex gap-2">
                    <button type="submit" className="btn btn-brand flex-grow-1" disabled={loading}>
                      {loading
                        ? 'Processando...'
                        : mode === 'login'
                        ? 'Entrar no portal'
                        : 'Criar conta'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-light"
                      onClick={() => navigate('/')}
                    >
                      Voltar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Access;
